<template>
  <v-app-bar flat color="surface" class="app-header" elevation="1" :absolute="false">
    <!-- Boton de menu hamburguesa (solo cuando sidebar esta colapsado) -->
    <v-app-bar-nav-icon
      v-if="!sidebarOpen"
      @click="$emit('toggle-sidebar')"
    />

    <!-- Selector de Sucursal (en vistas de emisión para roles con acceso) -->
    <div v-if="mostrarSelectorSucursal" class="d-flex align-center ml-2">
      <v-icon size="20" class="mr-2 text-grey">mdi-store</v-icon>
      <v-select
        v-model="sucursalSeleccionada"
        :items="sucursalesFiltradasPorRol"
        item-title="nombre"
        item-value="id"
        label="Sucursal"
        density="compact"
        variant="outlined"
        hide-details
        class="sucursal-select"
        :style="{ minWidth: '200px', maxWidth: '280px' }"
        :disabled="selectorBloqueado"
        :readonly="selectorBloqueado"
      >
        <template #selection="{ item }">
          <span class="text-body-2">{{ item.raw.nombre }}</span>
          <v-icon v-if="selectorBloqueado" size="14" class="ml-1 text-grey">mdi-lock</v-icon>
        </template>
        <template #item="{ item, props }">
          <v-list-item v-bind="props">
            <template #prepend>
              <v-icon size="18" color="primary">mdi-store-outline</v-icon>
            </template>
            <v-list-item-subtitle v-if="item.raw.direccion" class="text-caption">
              {{ item.raw.direccion }}
            </v-list-item-subtitle>
          </v-list-item>
        </template>
      </v-select>
    </div>

    <v-spacer />

    <!-- Selector de Tema -->
    <v-menu offset-y :close-on-content-click="false">
      <template v-slot:activator="{ props }">
        <v-btn
          v-bind="props"
          icon
          variant="text"
          class="mr-1"
          :title="isDarkTheme ? 'Tema Oscuro' : 'Tema Claro'"
        >
          <v-icon>{{ isDarkTheme ? 'mdi-moon-waning-crescent' : 'mdi-white-balance-sunny' }}</v-icon>
        </v-btn>
      </template>
      <v-card min-width="280" class="pa-2">
        <v-card-title class="text-subtitle-1 pb-1">
          <v-icon start size="20">mdi-palette</v-icon>
          Apariencia
        </v-card-title>

        <!-- Selector de Modo (Claro/Oscuro) -->
        <v-card-text class="pb-2">
          <div class="text-caption text-grey mb-2">Modo</div>
          <v-btn-toggle
            v-model="selectedTheme"
            mandatory
            color="primary"
            density="compact"
            class="w-100"
          >
            <v-btn value="lightTheme" class="flex-grow-1">
              <v-icon start size="18">mdi-white-balance-sunny</v-icon>
              Claro
            </v-btn>
            <v-btn value="darkTheme" class="flex-grow-1">
              <v-icon start size="18">mdi-moon-waning-crescent</v-icon>
              Oscuro
            </v-btn>
          </v-btn-toggle>
        </v-card-text>

        <!-- Selector de Variante de Color -->
        <v-card-text class="pt-0">
          <div class="text-caption text-grey mb-2">Color</div>
          <div class="d-flex flex-wrap ga-2">
            <v-btn
              v-for="variant in colorVariants"
              :key="variant.value"
              :color="variant.color"
              :variant="selectedVariant === variant.value ? 'flat' : 'tonal'"
              icon
              size="32"
              @click="changeVariant(variant.value)"
              :title="variant.title"
            >
              <v-icon v-if="selectedVariant === variant.value" size="16">mdi-check</v-icon>
            </v-btn>
          </div>
        </v-card-text>
      </v-card>
    </v-menu>

    <!-- Badge de Ambiente - Responsive -->
    <v-chip
      :color="ambiente === '00' ? 'warning' : 'success'"
      :size="$vuetify.display.xs ? 'x-small' : 'small'"
      label
      class="mr-2 mr-sm-4 ambiente-chip"
    >
      <v-icon start :size="$vuetify.display.xs ? 12 : 14">
        {{ ambiente === '00' ? 'mdi-test-tube' : 'mdi-shield-check' }}
      </v-icon>
      <!-- Texto completo en desktop, corto en móvil -->
      <span class="d-none d-sm-inline">AMBIENTE: {{ ambiente === '00' ? 'PRUEBAS' : 'PRODUCTIVO' }}</span>
      <span class="d-sm-none">{{ ambiente === '00' ? 'TEST' : 'PROD' }}</span>
    </v-chip>

    <!-- Menú de Usuario -->
    <v-menu offset-y>
      <template v-slot:activator="{ props }">
        <v-btn
          v-bind="props"
          variant="text"
          class="user-menu-btn"
        >
          <v-avatar color="primary" size="36" class="mr-2">
            <v-img
              v-if="emisorLogoUrl"
              :src="emisorLogoUrl"
              alt="Logo"
              cover
            />
            <span v-else class="text-body-2 font-weight-bold text-white">
              {{ userInitials }}
            </span>
          </v-avatar>
          <div class="text-left d-none d-md-block">
            <p class="text-body-2 font-weight-medium mb-0">{{ userName }}</p>
            <p class="text-caption text-grey mb-0">{{ userRole }}</p>
          </div>
          <v-icon class="ml-2">mdi-chevron-down</v-icon>
        </v-btn>
      </template>

      <v-list density="compact" nav>
        <v-list-item
          prepend-icon="mdi-account"
          title="Mi Perfil"
          @click="irAPerfil"
        />
        <v-divider class="my-1" />
        <v-list-item
          prepend-icon="mdi-logout"
          title="Cerrar Sesión"
          @click="logout"
          class="text-error"
        />
      </v-list>
    </v-menu>
  </v-app-bar>
</template>

<script>
import { ref, computed, watch } from 'vue'
import { useStore } from 'vuex'
import { useRouter, useRoute } from 'vue-router'
import { useTheme } from 'vuetify'
import { updateThemeVariant } from '@/plugins/vuetify'

export default {
  name: 'AppHeader',
  props: {
    sidebarOpen: {
      type: Boolean,
      default: true
    }
  },
  emits: ['toggle-sidebar'],
  setup() {
    const store = useStore()
    const router = useRouter()
    const route = useRoute()
    const vuetifyTheme = useTheme()

    // =========================================
    // TEMA Y APARIENCIA
    // =========================================
    const THEME_STORAGE_KEY = 'dte-theme-preference'

    // Variantes de color disponibles
    const colorVariants = [
      { value: 'default', title: 'Azul Corporativo', color: '#1565C0' },
      { value: 'indigo', title: 'Índigo', color: '#3949AB' },
      { value: 'teal', title: 'Verde Azulado', color: '#00897B' },
      { value: 'green', title: 'Verde', color: '#2E7D32' },
      { value: 'orange', title: 'Naranja', color: '#EF6C00' },
      { value: 'purple', title: 'Morado', color: '#7B1FA2' }
    ]

    // Cargar preferencias guardadas
    const loadSavedPreferences = () => {
      try {
        const saved = localStorage.getItem(THEME_STORAGE_KEY)
        if (saved) {
          return JSON.parse(saved)
        }
      } catch (e) {
        // Ignorar errores
      }
      return { theme: 'lightTheme', variant: 'default' }
    }

    const savedPref = loadSavedPreferences()
    const selectedTheme = ref(savedPref.theme || 'lightTheme')
    const selectedVariant = ref(savedPref.variant || 'default')

    // Computed para saber si está en modo oscuro
    const isDarkTheme = computed(() => selectedTheme.value === 'darkTheme')

    // Guardar preferencias en localStorage
    const savePreferences = () => {
      try {
        localStorage.setItem(THEME_STORAGE_KEY, JSON.stringify({
          theme: selectedTheme.value,
          variant: selectedVariant.value
        }))
      } catch (e) {
        // Ignorar errores
      }
    }

    // Cambiar tema (claro/oscuro)
    watch(selectedTheme, (newTheme) => {
      vuetifyTheme.global.name.value = newTheme
      savePreferences()
    })

    // Cambiar variante de color
    const changeVariant = (variantValue) => {
      selectedVariant.value = variantValue
      updateThemeVariant(vuetifyTheme, variantValue)
      savePreferences()
    }

    // Usuario
    const ambiente = computed(() => store.getters['auth/ambiente'])
    const userName = computed(() => store.getters['auth/userName'])
    const userRole = computed(() => {
      const user = store.getters['auth/currentUser']
      return user?.rol || 'Usuario'
    })
    const userInitials = computed(() => {
      const name = userName.value || 'U'
      const parts = name.split(' ')
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase()
      }
      return name.substring(0, 2).toUpperCase()
    })

    // Logo del emisor
    const emisorLogoUrl = computed(() => store.getters['emisor/emisorActual']?.logoUrl || null)

    // =========================================
    // SELECTOR DE SUCURSAL (para vistas de emisión)
    // =========================================
    // Rutas donde mostrar el selector
    const rutasEmision = ['emision-factura', 'emision-ccf']

    // Obtener el usuario actual
    const usuarioActual = computed(() => store.getters['auth/currentUser'])

    // Roles que pueden ver el selector de sucursal
    const rolesConSelectorSucursal = ['EmisorAdmin', 'GerenteSucursal', 'Cajero', 'Contador', 'Auditor']

    // Verificar si el usuario tiene un rol que puede ver el selector
    const tieneRolConSelector = computed(() => {
      const rol = usuarioActual.value?.rol
      return rolesConSelectorSucursal.includes(rol)
    })

    // Mostrar selector en rutas de emisión para roles con acceso
    const mostrarSelectorSucursal = computed(() => {
      const rutaActual = route.name
      return tieneRolConSelector.value && rutasEmision.includes(rutaActual)
    })

    // Verificar si el selector está bloqueado (desde el store)
    const selectorBloqueado = computed(() => {
      return store.getters['emisor/selectorSucursalBloqueado']
    })

    // Establecimientos disponibles para el usuario (todos del emisor)
    const establecimientosDisponibles = computed(() => {
      const establecimientos = store.getters['emisor/establecimientosDelEmisor'] || []
      // Ordenar para que la Casa Matriz (tipo '01') aparezca primero
      return [...establecimientos].sort((a, b) => {
        const aEsPrincipal = a.tipoEstablecimiento === '01' || a.catTipoEstablecimientoId === 1
        const bEsPrincipal = b.tipoEstablecimiento === '01' || b.catTipoEstablecimientoId === 1
        if (aEsPrincipal && !bEsPrincipal) return -1
        if (!aEsPrincipal && bEsPrincipal) return 1
        return 0
      })
    })

    // Sucursales filtradas según el rol del usuario
    // - EmisorAdmin: todas las sucursales
    // - GerenteSucursal/Cajero: solo las asignadas
    const sucursalesFiltradasPorRol = computed(() => {
      const rol = usuarioActual.value?.rol
      const todasLasSucursales = establecimientosDisponibles.value

      // EmisorAdmin ve todas las sucursales
      if (rol === 'EmisorAdmin') {
        return todasLasSucursales
      }

      // GerenteSucursal y Cajero ven solo las sucursales asignadas
      // Las sucursales asignadas están en el getter establecimientosUsuario
      const sucursalesUsuario = store.getters['emisor/establecimientosUsuario'] || []
      const idsAsignados = sucursalesUsuario.map(s => s.id)

      // Filtrar las sucursales disponibles por las asignadas al usuario
      const filtradas = todasLasSucursales.filter(s => idsAsignados.includes(s.id))

      // Si no hay filtradas pero hay sucursales del usuario, usar esas directamente
      if (filtradas.length === 0 && sucursalesUsuario.length > 0) {
        return sucursalesUsuario
      }

      return filtradas
    })

    // Sucursal seleccionada (vinculada al store)
    const sucursalSeleccionada = computed({
      get: () => store.getters['emisor/establecimientoActual']?.id || null,
      set: (id) => {
        if (id) {
          store.dispatch('emisor/selectEstablecimiento', id)
        }
      }
    })

    // Watch para seleccionar automáticamente la sucursal principal cuando:
    // 1. No hay sucursal seleccionada
    // 2. Hay establecimientos disponibles
    // 3. Estamos en una vista de emisión
    watch(
      [establecimientosDisponibles, mostrarSelectorSucursal],
      ([establecimientos, mostrar]) => {
        if (mostrar && establecimientos.length > 0) {
          // Si ya hay una seleccionada, re-dispararla para que v-select haga match con los items
          const actualId = sucursalSeleccionada.value
          if (actualId && establecimientos.some(e => e.id === actualId)) {
            store.dispatch('emisor/selectEstablecimiento', actualId)
          } else {
            // No hay selección o la guardada no existe: buscar Casa Matriz o la primera
            const principal = establecimientos.find(
              e => e.tipoEstablecimiento === '01' || e.catTipoEstablecimientoId === 1
            ) || establecimientos[0]
            if (principal) {
              store.dispatch('emisor/selectEstablecimiento', principal.id)
            }
          }
        }
      },
      { immediate: true }
    )

    // Logout
    const logout = async () => {
      await store.dispatch('auth/logout')
      await store.dispatch('emisor/clearAll')
      router.push({ name: 'login' })
    }

    // Navegación a Mi Perfil
    const irAPerfil = () => {
      router.push({ name: 'perfil' })
    }

    return {
      // Tema
      colorVariants,
      selectedTheme,
      selectedVariant,
      isDarkTheme,
      changeVariant,
      // Usuario
      ambiente,
      userName,
      userRole,
      userInitials,
      emisorLogoUrl,
      // Selector de sucursal
      mostrarSelectorSucursal,
      establecimientosDisponibles,
      sucursalesFiltradasPorRol,
      sucursalSeleccionada,
      selectorBloqueado,
      // Métodos
      logout,
      irAPerfil
    }
  }
}
</script>

<style scoped>
.app-header {
  border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.08) !important;
  position: static !important;
  flex-shrink: 0;
  width: 100% !important;
  left: 0 !important;
  right: 0 !important;
  background: linear-gradient(90deg, rgb(var(--v-theme-surface)) 0%, rgba(var(--v-theme-surface), 0.98) 100%) !important;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
}

/* Forzar que el toolbar interno ocupe todo el ancho */
.app-header :deep(.v-toolbar__content) {
  width: 100% !important;
}

/* Ajustes para hacer los elementos del header un poco más grandes */
.app-header :deep(.v-field) {
  font-size: 0.9rem !important;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.app-header :deep(.v-field:hover) {
  border-color: rgba(var(--v-theme-primary), 0.4) !important;
}

.app-header :deep(.v-field--focused) {
  box-shadow: 0 0 0 3px rgba(var(--v-theme-primary), 0.12) !important;
}

.app-header :deep(.v-field__input) {
  font-size: 0.9rem !important;
  min-height: 38px !important;
}

.app-header :deep(.v-btn) {
  font-size: 0.85rem !important;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.app-header :deep(.v-btn:hover) {
  transform: translateY(-1px);
}

.app-header :deep(.v-btn:active) {
  transform: translateY(0) scale(0.98);
}

.app-header :deep(.v-chip) {
  font-size: 0.8rem !important;
  height: 28px !important;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.app-header :deep(.v-icon) {
  font-size: 1.25rem !important;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.app-header :deep(.text-body-2) {
  font-size: 0.85rem !important;
}

.app-header :deep(.text-caption) {
  font-size: 0.75rem !important;
}

.app-header :deep(.v-avatar) {
  font-size: 0.9rem !important;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 8px rgba(var(--v-theme-primary), 0.2);
}

.app-header :deep(.v-avatar:hover) {
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(var(--v-theme-primary), 0.3);
}

/* Botón de menú de usuario - Estilo premium */
.user-menu-btn {
  text-transform: none;
  letter-spacing: normal;
  border-radius: 12px !important;
  padding: 8px 14px !important;
  min-height: 52px !important;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.user-menu-btn:hover {
  background: rgba(var(--v-theme-primary), 0.1) !important;
  box-shadow: 0 8px 32px rgba(var(--v-theme-primary), 0.35), 0 4px 12px rgba(0, 0, 0, 0.15) !important;
}

/* Badge de ambiente con efecto */
.ambiente-chip {
  flex-shrink: 0;
  font-weight: 600 !important;
  letter-spacing: 0.02em;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.ambiente-chip:hover {
  transform: scale(1.02);
}

/* Efecto glow para ambiente productivo */
.ambiente-chip.v-chip--color-success {
  box-shadow: 0 2px 12px rgba(16, 185, 129, 0.25);
}

/* Efecto glow para ambiente pruebas */
.ambiente-chip.v-chip--color-warning {
  box-shadow: 0 2px 12px rgba(245, 158, 11, 0.25);
  animation: pulse-warning 3s ease-in-out infinite;
}

@keyframes pulse-warning {
  0%, 100% { box-shadow: 0 2px 12px rgba(245, 158, 11, 0.25); }
  50% { box-shadow: 0 2px 20px rgba(245, 158, 11, 0.4); }
}

/* Dark mode ajustes */
.v-theme--dark .app-header,
.v-theme--darkTheme .app-header {
  background: linear-gradient(90deg, #0F172A 0%, rgba(15, 23, 42, 0.98) 100%) !important;
  border-bottom-color: rgba(255, 255, 255, 0.06) !important;
}

/* Selector de sucursal */
.sucursal-select {
  transition: all 0.2s ease;
}

.sucursal-select :deep(.v-field) {
  border-radius: 8px !important;
  font-size: 0.875rem !important;
}

.sucursal-select :deep(.v-field__input) {
  padding-top: 6px !important;
  padding-bottom: 6px !important;
  min-height: 36px !important;
}

.sucursal-select :deep(.v-field--focused) {
  box-shadow: 0 0 0 2px rgba(var(--v-theme-primary), 0.2) !important;
}

/* Ocultar solo en móviles muy pequeños (menos de 360px) */
@media (max-width: 360px) {
  .sucursal-select {
    display: none !important;
  }
}

/* Ajustar ancho en pantallas pequeñas */
@media (max-width: 600px) {
  .sucursal-select {
    min-width: 150px !important;
    max-width: 180px !important;
  }
}
</style>
